import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const bookingReviewFields = [
  { key: 'pickupLocation', label: 'Pickup Location', icon: 'bi-geo-alt-fill' },
  { key: 'pickupDate', label: 'Pick-up Date', icon: 'bi-calendar2-check' },
  { key: 'pickupTime', label: 'Pick-up Time', icon: 'bi-clock-history' },
  { key: 'dropoffLocation', label: 'Drop-off Location', icon: 'bi-geo-alt' },
  { key: 'dropoffDate', label: 'Drop-off Date', icon: 'bi-calendar2-check' },
  { key: 'dropoffTime', label: 'Drop-off Time', icon: 'bi-clock' },
  { key: 'q', label: 'Matched Vehicle Search', icon: 'bi-car-front' },
  { key: 'vehicleIntent', label: 'Vehicle Preference', icon: 'bi-car-front-fill' },
];

function readBookingParams(search) {
  const searchParams = new URLSearchParams(search);
  return bookingReviewFields.reduce((values, field) => {
    values[field.key] = (searchParams.get(field.key) || '').trim();
    return values;
  }, {});
}

function buildVehiclesPath(values) {
  const params = new URLSearchParams();
  bookingReviewFields.forEach((field) => {
    const value = values[field.key];
    if (value) params.set(field.key, value);
  });

  const queryString = params.toString();
  return queryString ? `/vehicles?${queryString}` : '/vehicles';
}

function BookingReview({ userRole }) {
  const location = useLocation();
  const navigate = useNavigate();

  const bookingValues = useMemo(() => readBookingParams(location.search), [location.search]);
  const hasBookingDetails = Object.values(bookingValues).some(Boolean);
  const vehiclesPath = useMemo(() => buildVehiclesPath(bookingValues), [bookingValues]);
  const authSearch = useMemo(() => {
    const params = new URLSearchParams({
      mode: 'login',
      next: vehiclesPath,
    });
    return params.toString();
  }, [vehiclesPath]);
  const signupSearch = useMemo(() => {
    const params = new URLSearchParams({
      mode: 'signup',
      next: vehiclesPath,
    });
    return params.toString();
  }, [vehiclesPath]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!hasBookingDetails) {
      navigate('/home');
      return;
    }

    if (userRole) {
      navigate(vehiclesPath);
      return;
    }

    navigate(`/auth?${authSearch}`);
  };

  if (!hasBookingDetails) {
    return (
      <div className="booking-page booking-review-page px-4 py-16">
        <section className="booking-review-empty card-luxury">
          <i className="bi bi-clipboard-x" aria-hidden="true" />
          <h1 className="section-title">No booking details were provided</h1>
          <p>Start from the booking form so we can prepare a complete request for review.</p>
          <Link to="/home" className="btn-luxury">
            Start Again <i className="bi bi-arrow-right" aria-hidden="true" />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="booking-page booking-review-page px-4 py-10">
      <section className="booking-review-hero card-luxury">
        <p className="drx-eyebrow">Booking Verification</p>
        <h1 className="section-title">Review Your Booking Request</h1>
        <p>
          Confirm that the journey details below are correct before continuing to the secure account step.
        </p>
      </section>

      <form className="booking-review-layout" onSubmit={handleSubmit}>
        <section className="booking-review-details card-luxury" aria-label="Booking request details">
          {bookingReviewFields.map((field) => {
            const value = bookingValues[field.key];
            if (!value) return null;

            return (
              <article className="booking-review-detail" key={field.key}>
                <i className={`bi ${field.icon}`} aria-hidden="true" />
                <span>{field.label}</span>
                <strong>{value}</strong>
              </article>
            );
          })}
        </section>

        <aside className="booking-summary booking-review-summary card-luxury">
          <h2>Confirmation</h2>
          <p>
            Submitting this request will continue with the exact details shown here.
            Verified accounts are required before any rental service can be used.
          </p>

          {!userRole && (
            <div className="booking-review-auth-note">
              <i className="bi bi-shield-lock" aria-hidden="true" />
              <span>Sign in or create an account before continuing.</span>
            </div>
          )}

          <button type="submit" className="btn-luxury booking-review-submit">
            Submit Booking Request <i className="bi bi-arrow-right" aria-hidden="true" />
          </button>

          {!userRole && (
            <Link to={`/auth?${signupSearch}`} className="btn-luxury-outline booking-review-secondary">
              Create Account Instead
            </Link>
          )}

          <Link to={`/home${location.search}`} className="booking-review-edit">
            <i className="bi bi-arrow-left" aria-hidden="true" /> Edit request
          </Link>
        </aside>
      </form>
    </div>
  );
}

export default BookingReview;
